// 文档：https://partner.coding.intlgame.com/p/ogdb-backend/wiki/9395#user-content-2-%E5%9C%A8%E5%90%88%E9%80%82%E7%9A%84%E5%9C%B0%E6%96%B9%E4%BD%BF%E7%94%A8hooks%E8%BF%94%E5%9B%9E%E7%9A%84decodeobj
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDeepCompareEffect, useDebounceFn, useAsyncEffect } from 'ahooks';
import {
  IFilterUrlConfigItem,
  TTransInputType,
  TUrlQueryKey,
} from '@intelligence/_config/components/filterUrl/kpiTrends';
import {
  writeShortUrlParams,
  getShortUrlParams,
  getShortIdUrlParams,
} from '@intelligence/services/common';

interface IFilterUrlLinkageProps {
  filterData: any;
  config: Record<TUrlQueryKey, IFilterUrlConfigItem>;
  // 是否为短连接id模式
  isShortIdMode?: boolean;
  // 是否为短链接模式，与needUpdateUrl配合使用
  isShortMode?: boolean;
  // 是否需要结构url，将结构后的参数返回
  needDecodeUrl?: boolean;
  // 是否需要更新url
  needUpdateUrl?: boolean;
}

const SHORT_KEY = 'filters_key';

const SHORT_ID_KEY = 'shortid';

/** 仅保留 config 声明的 query key，避免粒度收窄或短链合并后残留历史筛选参数 */
const pruneUrlParamsToConfig = (
  params: URLSearchParams,
  cfg: Record<TUrlQueryKey, IFilterUrlConfigItem>,
) => {
  Array.from(params.keys()).forEach((key) => {
    if (key !== SHORT_KEY && key !== SHORT_ID_KEY && !(key in cfg)) {
      params.delete(key);
    }
  });
};

export default function useFilterUrlLinkage(props: IFilterUrlLinkageProps) {
  const { filterData, config, isShortIdMode = false, isShortMode = false, needDecodeUrl = false, needUpdateUrl = false } = props;
  // 防止无限循环：跟踪当前是否正在更新URL
  const isUpdatingUrlRef = useRef<boolean>(false);
  // 防止重复 replaceState：跟踪上一次的 URL
  const lastUrlRef = useRef<string>('');
  // 浏览器的参数
  const [encodeStr, setEncodeStr] = useState<string>('');
  // 当短链接模式，未被转码前的url参数
  const [encodeOirginalStr, setEncodeOirginalStr] = useState<string>('');
  // 短链接模式的短链接id
  const [shortId, setShortId] = useState<string>('');
  // url转换回原对象
  const [decodeObj, setDecodeObj] = useState<any>(null);
  const [isUrlParamsUpdate, setIsUrlParamsUpdate] = useState<boolean>(false);
  const [isWriteLoading, setIsWriteLoading] = useState<boolean>(false);
  // 是否初始化中
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  /**
   * 根据时间粒度转换日期字符串
   * @param dateStr 日期字符串
   * @param granularity 时间粒度（day/daily/week/weekly/month/monthly）
   * @param part 指定返回周/月的开始日或结束日（默认为'start'）
   * @returns 根据粒度返回 Date | { start: Date; end: Date } | null
   */
  function parseDateByGranularity(
    dateStr: string,
    granularity:
      | 'day'
      | 'daily'
      | 'date'
      | 'week'
      | 'weekly'
      | 'month'
      | 'monthly'
      | 'hour'
      | 'hourly',
    part: 'start' | 'end' = 'start',
  ): Date | { start: Date; end: Date; } | null {
    // 尝试解析日期
    const date = new Date(dateStr);

    // 处理无效日期
    if (isNaN(date.getTime())) {
      throw new Error(`不支持的粒度类型: ${granularity}`);
    }

    // 根据粒度处理日期
    switch (granularity.toLowerCase()) {
      case 'hour':
      case 'hourly':
        return date;

      case 'day':
      case 'daily':
      case 'date':
        // 日粒度直接返回日期
        return date;

      case 'week':
      case 'weekly':
        // 计算周的起始日（周一）和结束日（周日）
        return getWeekRange(date, part);

      case 'month':
      case 'monthly':
        // 计算月的起始日和结束日
        return getMonthRange(date, part);

      default:
        throw new Error(`不支持的粒度类型: ${granularity}`);
    }
  }

  /**
   * 获取日期所在周的起始/结束日期
   * @param date 日期对象
   * @param part 返回类型（start/end）
   */
  function getWeekRange(
    date: Date,
    part: 'start' | 'end',
  ): Date | { start: Date; end: Date; } {
    // 计算周一（ISO 8601 标准，周一为一周第一天）
    const dayOfWeek = date.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // 周日需要特殊处理
    const start = new Date(date);
    start.setDate(date.getDate() + mondayOffset);
    start.setHours(0, 0, 0, 0);

    // 计算周日
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    const partMap = {
      start,
      end
    };

    return part in partMap ? partMap[part] : { start, end };
  }

  /**
   * 获取日期所在月的起始/结束日期
   * @param date 日期对象
   * @param part 返回类型（start/end）
   */
  function getMonthRange(
    date: Date,
    part: 'start' | 'end',
  ): Date | { start: Date; end: Date; } {
    // 当月第一天
    const start = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);

    // 当月最后一天（下月第0天即本月最后一天）
    const end = new Date(
      date.getFullYear(),
      date.getMonth() + 1,
      0,
      23,
      59,
      59,
      999,
    );

    const partMap = {
      start,
      end
    };

    return part in partMap ? partMap[part] : { start, end };
  }

  /**
   * 编码过滤器值
   *
   * 根据不同的输入类型将值转换为URL可用的字符串格式
   *
   * @param value - 需要编码的值，可以是任意类型
   * @param type - 值的类型，包括'startDate'、'endDate'、'oneToManyObject'、'array'等
   * @param config - 字段配置
   * @returns 编码后的字符串，如果值为空则返回空字符串
   *
   * @remarks
   * - 日期类型: 格式化为'YYYY-MM-DD'
   * - 一对多对象: 转换为'a::1,2;;b::3'格式
   * - 数组类型: 用逗号拼接元素
   * - 其他类型: 直接调用toString()
   */
  const encodeFilterValue = (
    value: any,
    type: TTransInputType,
    config?: IFilterUrlConfigItem,
    encodeContext?: { granularity?: string; },
  ) => {
    if (value === undefined || value === null || value === '') return '';
    switch (type) {
      case 'startDate':
      case 'endDate': {
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;
        const pad = (n: number) => n.toString().padStart(2, '0');
        const gran = encodeContext?.granularity?.toLowerCase();
        if (gran === 'hour' || gran === 'hourly') {
          return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
        }
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
      }
      case 'oneToManyObject':
        // 转换一对多对象：a:1,2;b:3
        return Object.entries(value)
          .map(([k, v]) => `${k}::${Array.isArray(v) ? v.join(',') : v}`)
          .join(';;');

      case 'array':
        // 数组类型直接拼接
        return value.join(',');

      case 'objectArray': {
        return value
          ?.map((item: any) => config?.serializeValue?.(item) ?? item)
          ?.filter((item: any) => item !== undefined && item !== null && item !== '')
          ?.join(',');
      }

      default: // string和其他类型
        return value.toString();
    }
  };

  /**
   * 解码过滤器值，根据不同类型进行相应处理
   * @param value - 原始值，通常为字符串数组
   * @param type - 值类型，决定处理逻辑
   * @param conditionValues - 条件值数组，用于日期解析等场景
   * @returns 处理后的值，类型根据输入类型而定
   *
   * 支持处理以下类型：
   * - 日期类型(startDate/endDate)：使用parseDateByGranularity解析
   * - 一对多对象(oneToManyObject)：格式为"key1::val1,val2;;key2::val3"
   * - 数组(array)：合并所有值并展平
   * - 布尔值(boolean)：转换为布尔类型
   * - 默认情况：直接取第一个值
   */
  const decodeFilterValue = (
    value: any,
    type: TTransInputType,
    conditionValues: any,
    config?: any,
  ) => {
    switch (type) {
      case 'startDate':
      case 'endDate':
        return parseDateByGranularity(value[0], conditionValues[0], type === 'startDate' ? 'start': 'end');

      case 'oneToManyObject': {
        // 处理一对多对象：key1:val1,val2;key2:val3
        const processedValue: any = {};
        value[0]?.split(';;')?.forEach((pair: string) => {
          const [key, values] = pair.split('::');
          if (key && values) {
            processedValue[key] = values.split(',');
          }
        });
        return processedValue;
      }

      case 'array': {
        // 处理数组（合并所有值）并移除空字符串
        return value
          .flatMap((str: string) => str.split(','))
          .filter(Boolean);
      }

      case 'objectArray': {
        const flattened = value
          .flatMap((str: string) => {
            return str.split(',');
          })
          ?.filter(Boolean);
        return flattened
          ?.map((item: any) => config?.deserializeValue?.(item) ?? item)
          ?.filter(Boolean);
      }

      case 'boolean': {
        return value[0] === 'true';
      }

      default: {
        // 字符串类型直接取第一个值
        return value[0];
      }
    }
  };

  /**
   * 将表单数据转换为URL查询参数
   *
   * @param formData - 需要转换的表单数据对象
   * @param config - 配置对象，定义每个URL参数的转换规则
   * @returns 转换后的URL参数字符串
   *
   * @remarks
   * 1. 根据config中的inputPath递归获取表单数据中的嵌套值
   * 2. 使用encodeFilterValue根据input类型对值进行编码处理
   * 3. 支持数组参数的批量添加
   * 4. 错误处理会捕获并记录转换过程中的异常
   */
  const transFormDataToUrlParams = async (
    formData: any,
    config: Record<TUrlQueryKey, IFilterUrlConfigItem>,
  ) => {
    const params = new URLSearchParams();

    Object.keys(config).forEach((key: TUrlQueryKey) => {
      const { input, inputPath } = config[key];
      try {
        // 解析inputPath路径
        const pathSegments = inputPath.split('/');
        let value = formData;

        // 递归获取嵌套值
        for (const seg of pathSegments) {
          if (!value) break;
          const index =
            typeof seg === 'string' && !isNaN(Number(seg)) ? Number(seg) : seg;
          value = value[index];
        }


        // 根据input类型转换值
        const processedValue = encodeFilterValue(value, input);

        // 最终参数处理
        if (Array.isArray(processedValue)) {
          processedValue.forEach((v) => params.append(key, v));
        } else {
          params.append(key, processedValue);
        }
      } catch (error) {
        console.error(`处理配置项 ${inputPath} 时出错:`, error);
      }
    });
    return params?.toString();
  };

  /**
     * 合并新旧URL参数
     * @param newParamsStr 新URL参数字符串
     * @param oldParamsStr 旧URL参数字符串
     * @returns 合并后的URL参数字符串
     */
  const mergeUrlParams = (newParamsStr: string, oldParamsStr: string) =>  {
    // 创建URLSearchParams对象解析参数
    const newParams = new URLSearchParams(newParamsStr);
    const oldParams = new URLSearchParams(oldParamsStr);
    const mergedParams = new URLSearchParams(oldParams.toString());

    // 遍历新参数，更新或添加值
    newParams.forEach((value, key) => {
      mergedParams.set(key, value);
    });

    return mergedParams;
  };

  /**
   * 将URL参数解析回表单对象
   * @param {string} urlParams URL参数字符串
   * @param {Array} config 转换配置
   * @returns {Object} 还原后的表单对象
   */
  const transUrlStringToFormData = async (urlParams: string, config: Record<TUrlQueryKey, IFilterUrlConfigItem>) => {
    let params = new URLSearchParams(urlParams);
    const shortKey = params.get(SHORT_KEY);

    // 如果为短链接模式，将短链接解构的同时，加上可能存在浏览器中的参数
    if (shortKey) {
      try {
        const res = await getShortUrlParams({ key: shortKey });
        const data = JSON.parse(res?.data?.params ?? '{}');
        params.delete(SHORT_KEY);
        params = mergeUrlParams(params.toString(), data);
      } catch (error) {
        console.error('获取短链接参数失败:', error);
        params.delete(SHORT_KEY);
      }
    }

    const formData = {};

    // 辅助函数：设置嵌套对象值
    /**
     * 深度设置嵌套对象/数组的属性值
     * @param obj - 目标对象/数组
     * @param pathSegments - 属性路径数组（支持数字索引表示数组元素）
     * @param value - 要设置的值
     * @description
     * 1. 自动创建路径中不存在的中间对象/数组
     * 2. 处理数组索引时会自动扩容
     * 3. 根据下一路径段类型智能初始化空对象{}或数组[]
     */
    const setNestedValue = (obj, pathSegments, value) => {
      let current = obj;
      for (let i = 0;i < pathSegments.length;i++) {
        const seg = pathSegments[i];
        const isLast = i === pathSegments.length - 1;

        if (isLast) {
          current[seg] = value;
        } else {
          const nextSeg = pathSegments[i + 1];
          const isNextNumber = !isNaN(nextSeg);

          if (!current[seg]) {
            current[seg] = isNextNumber ? [] : {};
          }

          // 处理数组索引（需确保长度足够）
          if (Array.isArray(current[seg]) && !isNaN(seg)) {
            const index = Number(seg);
            if (index >= current[seg].length) {
              current[seg][index] = isNextNumber ? [] : {};
            }
          }

          current = current[seg];
        }
      }
    };

    /**
     * 根据查询参数键名和输入类型获取条件值
     * @param key - URL查询参数键名
     * @param inputType - 输入类型（如'startDate'/'endDate'）
     * @param defaultGranularity - 默认粒度值（当条件值为空时使用）
     * @returns 处理后的条件值数组
     */
    const getConditionValue = (
      key: TUrlQueryKey,
      inputType: TTransInputType,
      defaultGranularity: any,
    ) => {
      const conditionValues = (key && params.getAll(key)) || [];
      if (['startDate', 'endDate'].includes(inputType)) {
        return conditionValues.length === 0
          ? [defaultGranularity]
          : conditionValues;
      }
      return conditionValues;
    };

    Object.keys(config).forEach((key: TTransInputType) => {
      const { input, inputPath, defaultGranularity, outputConditionKey } = config[key];
      try {
        // 获取参数值（处理多值情况）
        const paramValues = params.getAll(key);
        if (paramValues.length === 0) return;

        const conditionValues = getConditionValue(
          outputConditionKey,
          input,
          defaultGranularity,
        );

        const processedValue = decodeFilterValue(
          paramValues,
          input,
          conditionValues,
          config?.[key],
        );
        // console.log(key, processedValue);

        // 解析inputPath（注意此处使用原配置的inputPath作为目标路径）
        const pathSegments = inputPath
          .split('/')
          .map((seg) => (isNaN(seg) ? seg : Number(seg)));

        // 设置嵌套值
        setNestedValue(formData, pathSegments, processedValue);
      } catch (error) {
        console.error(`解析配置项 ${inputPath} 时出错:`, error);
      }
    });
    setDecodeObj(formData);
    setIsUrlParamsUpdate(true);
    setIsLoading(false);
    return formData;
  };

  // 注意updateObj需要传入全量的值，不能只传入修改的那个
  /**
   * 更新URL查询参数
   *
   * @param updateObj - 包含需要更新的值的对象
   * @param config - URL参数配置对象，定义参数名与数据路径的映射关系
   * @param isShortMode - 是否启用短链接模式（可选）
   * @param url - 要处理的URL字符串（可选，默认使用当前location.search）
   *
   * @description
   * 1. 根据config配置解析updateObj中的值
   * 2. 将值转换为URL参数格式
   * 3. 支持短链接模式：当启用时会将参数压缩为短键
   * 4. 最终通过 history.replaceState 更新浏览器 URL（避免 pushState 额外占用后退栈）
   *
   * @throws 处理配置项出错时会打印错误日志
   */
  const updateUrl = async (updateObj: any = {}, config: Record<TUrlQueryKey, IFilterUrlConfigItem>, isShortMode?: boolean, url?: string) => {
    let params = new URLSearchParams(url || location.search);
    pruneUrlParamsToConfig(params, config);
    // 注意这里只能循环config，因为updateObj表示的是值，config中才有相对updateObj的路径
    Object.keys(config).forEach((key: TUrlQueryKey) => {
      const { input, inputPath } = config[key] || {};
      try {
        // 解析inputPath路径
        const pathSegments = inputPath.split('/');
        let value = updateObj;

        // 递归获取嵌套值
        for (const seg of pathSegments) {
          if (!value) break;
          const index =
            typeof seg === 'string' && !isNaN(Number(seg)) ? Number(seg) : seg;
          value = value[index];
        }
        // 根据input类型转换值
        const processedValue = encodeFilterValue(value, input, config?.[key], {
          granularity: updateObj?.rangeTime?.type,
        });
        // console.log(key, value);

        // 最终参数处理
        if (Array.isArray(processedValue)) {
          processedValue.forEach((v) => params.has(key) ? params.set(key, v) : params.append(key, v));
        } else {
          params.has(key) ? params.set(key, processedValue) : params.append(key, processedValue);
        }
      } catch (error) {
        console.error(`处理配置项 ${inputPath} 时出错:`, error);
      }
    });
    let paramsWithoutShortKey = new URLSearchParams(params.toString());
    paramsWithoutShortKey.delete(SHORT_KEY);

    let sufix = paramsWithoutShortKey.toString();
    if (isShortMode) {
      try {
        if (params.has(SHORT_KEY)) {
          const shortKey = params.get(SHORT_KEY) || '';
          const res = await getShortUrlParams({ key: shortKey });
          const data = JSON.parse(res?.data?.params ?? '{}');
          params.delete(SHORT_KEY);
          params = mergeUrlParams(params.toString(), data);
          pruneUrlParamsToConfig(params, config);
          paramsWithoutShortKey = new URLSearchParams(params.toString());
          paramsWithoutShortKey.delete(SHORT_KEY);
        }
        const result = await writeShortUrlParams({
          system_type: 'intelligence',
          params: JSON.stringify(paramsWithoutShortKey.toString()) || '{}',
        });
        const shortLinkId = result?.data?.key ?? '';
        if (shortLinkId) {
          params.set(SHORT_KEY, shortLinkId);
        } else {
          params.delete(SHORT_KEY);
        }
        sufix = params.toString();
      } catch (error) {
        console.error('短链接模式处理失败:', error);
        params.delete(SHORT_KEY);
        sufix = paramsWithoutShortKey.toString();
      }
    } else {
      params.delete(SHORT_KEY);
      sufix = params.toString();
    }
    // 防止重复 replaceState：仅当 URL 真正变化时才更新
    const newUrl = `?${sufix}`;
    if (newUrl !== lastUrlRef.current && !isUpdatingUrlRef.current) {
      isUpdatingUrlRef.current = true;
      lastUrlRef.current = newUrl;
      try {
        history.replaceState({}, '', newUrl);
      } catch {
      } finally {
        isUpdatingUrlRef.current = false;
      }
    }
  };

  /**
   * 设置URL参数并更新浏览器地址栏
   *
   * 根据是否启用短链模式：
   * 1. 短链模式：将过滤参数转换为短链key并更新URL
   * 2. 普通模式：直接使用原始参数更新URL
   *
   * @async
   * @function setUrl
   * @returns {Promise<void>}
   */
  const setUrl = async () => {
    try {
      const originalUrlStr: string = await transFormDataToUrlParams(filterData, config);
      const params = new URLSearchParams(originalUrlStr);
      params.delete(SHORT_KEY);
      let sufix = params.toString();
      if (isShortMode) {
        try {
          const result = await writeShortUrlParams({
            system_type: 'intelligence',
            params: JSON.stringify(sufix) || '{}',
          });
          const shortLinkId = result?.data?.key ?? '';
          if (shortLinkId) {
            params.set(SHORT_KEY, shortLinkId);
            sufix = params.toString();
          } else {
            params.delete(SHORT_KEY);
            sufix = params.toString();
          }
          setShortId(shortLinkId);
        } catch (error) {
          console.error('写入短链接参数失败:', error);
          params.delete(SHORT_KEY);
          sufix = params.toString();
          setShortId('');
        }
        setIsWriteLoading(false);
      } else {
        params.delete(SHORT_KEY);
        sufix = params.toString();
        setShortId('');
        setIsWriteLoading(false);
      }
      setEncodeStr(sufix);
      setEncodeOirginalStr(originalUrlStr);
      setIsLoading(false);
      // 防止重复 replaceState：仅当 URL 真正变化时才更新
      const newUrl = `?${sufix}`;
      if (newUrl !== lastUrlRef.current && !isUpdatingUrlRef.current) {
        isUpdatingUrlRef.current = true;
        lastUrlRef.current = newUrl;
        try {
          history.replaceState({}, '', newUrl);
        } catch (e) {
        } finally {
          isUpdatingUrlRef.current = false;
        }
      }
    } catch (error) {
      console.error('setUrl 执行失败:', error);
      setIsLoading(false);
      setIsWriteLoading(false);
    }
  };

  const { run: runUpdateUrl, cancel: cancelUpdateUrl } = useDebounceFn(setUrl, {
    wait: 500,
  });

  const { run: runUpdateFormData, cancel: cancelUpdateFormData } = useDebounceFn(
    transUrlStringToFormData,
    {
      wait: 500,
    },
  );

  useEffect(() => {
    return () => {
      cancelUpdateUrl();
      cancelUpdateFormData();
    };
  }, [cancelUpdateFormData, cancelUpdateUrl]);

  useAsyncEffect(async () => {
    try {
      if (needDecodeUrl && location?.search) {
        const params = new URLSearchParams(location.search.substring(1));
        const shortIdKey = params.get(SHORT_ID_KEY) || '';
        if (!isShortIdMode || !shortIdKey) {
          runUpdateFormData(location.search, config);
          return;
        }

        const res = await getShortIdUrlParams({ key: shortIdKey });
        const shortIdUrlParamsStr = res?.data?.short_url?.replace(/^(\?)|(&)/, '') || '';
        // 防止重复 replaceState：仅当 URL 真正变化时才更新
        const newUrl = `?${shortIdUrlParamsStr}`;
        if (newUrl !== lastUrlRef.current && !isUpdatingUrlRef.current) {
          isUpdatingUrlRef.current = true;
          lastUrlRef.current = newUrl;
          try {
            history.replaceState({}, '', newUrl);
          } catch (e) {
          } finally {
            isUpdatingUrlRef.current = false;
          }
        }
        runUpdateFormData(newUrl, config);
      } else if (needUpdateUrl && !isWriteLoading) {
        runUpdateUrl();
      }
    } catch (error) {
      console.error('useFilterUrlLinkage 初始化失败:', error);
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    encodeStr,
    encodeOirginalStr,
    shortId,
    decodeObj,
    isUrlParamsUpdate,
    updateUrl
  };
}
